/* iter55f84 — Coach hint content.
 *
 * A tiny catalogue of "what to do next" prompts, keyed by surface + a
 * derived state cue. Every entry is pure text (no JSX, no state
 * mutation) so callers can pass the current app state and get back a
 * `{label, body}` pair — nothing else. Keeps the hint copy version-
 * controlled in one place and lets a copy-editor sweep them without
 * touching component code.
 *
 * Voice: gentle mentor.
 * Style: short — one action title (≤5 words) + one mentor sentence
 *        (≤180 chars). Never scold, never assume, always end with
 *        an actionable next step.
 */

/**
 * Editor / manuscript body hints. `ctx` should include:
 *   { chapterWords, chapterCount, isEmpty, hasPendingSuggestions,
 *     suggestionCount, isDemo, canGenerate, canAiWriteChapters,
 *     tokensRemaining, activeMode, isStreaming }
 */
export function editorHint(ctx) {
  const c = ctx || {};

  if (c.isStreaming) {
    return {
      label: 'Claude is writing',
      body:
        'Sit back for a moment. When the stream finishes, you can tighten a line by ' +
        'selecting it — a small toolbar will offer polish and rewrite options.',
    };
  }

  if (c.isEmpty) {
    if (c.canAiWriteChapters) {
      return {
        label: 'Fresh chapter',
        body:
          'Two ways in: type your opening line here (auto-saves on blur) or hover ' +
          'the frame and hit "Draft with AI" to let Claude take the first pass.',
      };
    }
    return {
      label: 'Fresh chapter',
      body:
        'Start with your opening sentence — even a placeholder helps. Every save ' +
        'is versioned, so nothing you write can be lost.',
    };
  }

  if (c.hasPendingSuggestions) {
    const n = c.suggestionCount || 1;
    return {
      label: n === 1 ? 'Review a suggestion' : `Review ${n} suggestions`,
      body:
        'The highlighted phrases each carry an editorial note — amber for edits, ' +
        'sky for comments. Press Cmd/Ctrl+J to jump to the next one, or open the ' +
        'Suggestions drawer to accept, tweak, or dismiss without leaving the page.',
    };
  }

  if ((c.chapterWords || 0) < 250) {
    return {
      label: 'Toward the first milestone',
      body:
        'Aim for ~250 words in this chapter before switching modes — Story Bible ' +
        'and Continue both work best when they have real prose to pattern off.',
    };
  }

  if ((c.chapterWords || 0) < 1200) {
    return {
      label: 'Nudge the tone',
      body:
        'Try the "Darker" / "Lighter" / "More action" trio below to see three ' +
        'alternative endings. Keep only the one that echoes your intent.',
    };
  }

  return {
    label: 'Wrap the chapter',
    body:
      'Long enough to close? Toggle "The End" in the chapter toolbar to lock the ' +
      'closing beat, then jump to the next chapter from the sidebar.',
  };
}

/**
 * Chapters-list panel hints (per-chapter row hover). `ctx`:
 *   { number, wordCount, status, isActive, totalChapters }
 */
export function chapterRowHint(ctx) {
  const c = ctx || {};
  if (c.status === 'draft' && (c.wordCount || 0) === 0) {
    return {
      label: 'Empty chapter',
      body:
        'Click to open it. Every project needs one first line — start with a ' +
        'placeholder if you have to; the wordcount only shames you if you let it.',
    };
  }
  if (c.status === 'draft' && (c.wordCount || 0) < 500) {
    return {
      label: 'Draft in progress',
      body:
        'Under 500 words — still shaping up. Open it to keep drafting, or use ' +
        '"Continue" once your voice is set to have Claude match it.',
    };
  }
  if (c.status === 'draft') {
    return {
      label: 'Solid draft',
      body:
        'Ready for a pass? Open the chapter and run the editorial suggestions ' +
        'drawer — it flags redundancies, weak verbs, and pacing gaps.',
    };
  }
  if (c.status === 'edited' || c.status === 'final') {
    return {
      label: 'Locked-ish',
      body:
        'This one\'s marked as done. If you want to revise, open it and roll back ' +
        'to a specific version from the History drawer without losing anything.',
    };
  }
  return {
    label: 'Open chapter',
    body: 'Click to jump into this chapter. Every action here is auto-saved.',
  };
}

/**
 * Character bible list-row hints. `ctx`:
 *   { hasAttributes, hasBackstory, hasRelationships, isActive, name }
 */
export function characterRowHint(ctx) {
  const c = ctx || {};
  if (!c.hasAttributes) {
    return {
      label: 'Sketch this character',
      body:
        'Age, drive, and one unmistakable detail are the minimum. Even one line ' +
        'is enough for Claude to keep them consistent across chapters.',
    };
  }
  if (!c.hasBackstory) {
    return {
      label: 'Add a backstory',
      body:
        'A two-paragraph backstory gives Drafting mode the emotional weight to ' +
        'lean on — you\'ll notice the difference in the very next chapter.',
    };
  }
  if (!c.hasRelationships) {
    return {
      label: 'Map the relationships',
      body:
        'Even sketching two rivalries and one alliance sharpens dialogue enormously. ' +
        'Try the "+ Relation" button below.',
    };
  }
  return {
    label: 'Ready to write',
    body:
      'This character is fully sketched. Reference them in a chapter and Claude ' +
      'will lean on this bible automatically.',
  };
}

/**
 * Story-Bible / project workspace hint. `ctx`:
 *   { chapterCount, characterCount, hasVariables, wordsTotal }
 */
export function workspaceHint(ctx) {
  const c = ctx || {};
  if ((c.characterCount || 0) === 0) {
    return {
      label: 'Sketch a character',
      body:
        'One protagonist is enough to get started — open the Character Bible ' +
        'panel and give Claude someone to write about.',
    };
  }
  if (!c.hasVariables) {
    return {
      label: 'Pin your variables',
      body:
        'Setting, era, tone — pin these in the Variables panel so every generation ' +
        'inherits the same world instead of drifting.',
    };
  }
  if ((c.chapterCount || 0) === 0) {
    return {
      label: 'Draft your first chapter',
      body:
        'Open the Editor and start writing (or click "Draft with AI"). The first ' +
        'chapter defines the voice everything else will follow.',
    };
  }
  return {
    label: 'Keep moving',
    body:
      'You\'re past the setup — writing every day, even a paragraph, is what ' +
      'finishes novels. The wordcount badges track your streak.',
  };
}

/**
 * Toolbar button hints — one entry per known toolbar action. Keyed by a
 * stable `id` so ManuscriptToolbar can just look it up rather than
 * carrying the copy inline. Missing keys return `null` so the primitive
 * degrades to no-tooltip cleanly.
 */
const TOOLBAR_HINTS = {
  'font-family': {
    label: 'Reading typeface',
    body:
      'Purely visual — pick the face that puts you in the right frame. ' +
      'Your export uses whichever type your reader has set.',
  },
  'font-size': {
    label: 'Comfortable reading size',
    body:
      'Bump this up when your eyes get tired. Editor-only — exports keep ' +
      'standard trade sizes.',
  },
  'line-height': {
    label: 'Breathing room',
    body:
      'Denser lines feel more novel-like; looser lines catch more typos. ' +
      'Change it whenever you switch between drafting and editing.',
  },
  'text-align': {
    label: 'Alignment',
    body:
      'Justified feels published; left-aligned makes editing gaps easier ' +
      'to spot. Pick whichever helps you today.',
  },
  'continue': {
    label: 'Continue the scene',
    body:
      'Claude reads the last paragraph and adds one more in the same voice. ' +
      'Best after you\'ve set the tone yourself.',
  },
  'the-end': {
    label: 'Mark the chapter closed',
    body:
      'Locks the closing beat and adds a "The End" flourish on export. ' +
      'You can flip it back any time.',
  },
  'search': {
    label: 'Search the manuscript',
    body:
      'Find any phrase across chapters. Great for tracking recurring imagery ' +
      'or rooting out an overused word.',
  },
  'bookmarks': {
    label: 'Bookmark this spot',
    body:
      'Drop a marker so you can jump back after a break, or use it as a ' +
      '"pick up here tomorrow" note.',
  },
  'suggestions': {
    label: 'Editorial suggestions',
    body:
      'Claude (and any co-editors) flag redundancies, weak verbs, and pacing dips ' +
      'as anchored notes. The Live pill next to the count shows realtime connection ' +
      'status — new suggestions appear instantly without a refresh.',
  },
  // iter55g39 — Select-all gesture teaching hint.
  'select-all': {
    label: 'Select the whole chapter',
    body:
      'Hold Cmd (mac) or Ctrl (win/linux) and left-click anywhere in the manuscript ' +
      'body to select every paragraph at once. Then Cmd/Ctrl+C copies the entire ' +
      'chapter — handy for handing off to an editor or a beta reader.',
  },
  // iter55g36 — Jump-to-suggestion shortcut teaching hint.
  'jump-suggestion': {
    label: 'Jump to next suggestion',
    body:
      'Press Cmd/Ctrl+J to scroll to the next unresolved suggestion and briefly ' +
      'pulse its highlight. Shift+Cmd/Ctrl+J goes back. Works even when the ' +
      'Suggestions drawer is closed.',
  },
  // iter55g34 — Payouts ledger hint.
  'payouts': {
    label: 'Your payouts ledger',
    body:
      'Every bookstore sale writes a row here. Green = paid instantly via Stripe ' +
      'Connect at checkout, blue = paid on the manual sweep, amber = still pending. ' +
      'CSV export is one click away.',
  },
  'history': {
    label: 'Version history',
    body:
      'Every save is stamped. Roll back to any past version safely — nothing ' +
      'you\'ve written can be lost.',
  },
  'plagiarism': {
    label: 'Copyscape check',
    body:
      'Runs a plagiarism sweep against the public web. Uses a Copyscape ' +
      'credit; skip it for early drafts.',
  },
  'spellcheck': {
    label: 'Spellcheck this chapter',
    body:
      'Catches typos and homophones. Faster than reading top-to-bottom.',
  },
  'export': {
    label: 'Export this project',
    body:
      'Choose PDF (print-ready via Adobe), KDP bundle, or plain text. Big ' +
      'exports can render in the background and email you the link.',
  },
  // iter-coaching-refresh · Feb-2026 — new toolbar/button hints.
  'opus-review': {
    label: 'Opus editorial pass',
    body:
      'Fires Claude Opus 4.7 across every chapter. Suggestions land in the ' +
      'Notes drawer, bucketed by beats, pacing, tone, chapter hooks, ' +
      'cliffhangers, and character density. Admin-only.',
  },
  'sign-agreement': {
    label: 'Sign the Publishing Agreement',
    body:
      'Platform-wide author contract — 85/15 while your subscription is ' +
      'current, 70/30 if it lapses. Type your legal name, tick "I agree", ' +
      'and the admin countersigns. Executed PDF appears on your dashboard.',
  },
  'author-agreement-status': {
    label: 'Agreement status',
    body:
      'Not signed → Awaiting Admin Approval → Fully Executed. Once ' +
      'executed, download the tamper-sealed PDF from the same card.',
  },
  'stripe-live-mode': {
    label: 'Stripe live-mode banner',
    body:
      'Green pill = Live + webhook verified. Amber = Live but a piece is ' +
      'missing. Sky = Test. Red = Stripe not configured. Hover the hint for ' +
      'the exact env-var to set in the deployment UI.',
  },
  'suggestions-category-filter': {
    label: 'Filter suggestions by category',
    body:
      'When Opus does an edit pass, filter to one bucket — Cliffhanger, ' +
      'Pacing, Tone, etc. — to bulk-accept or bulk-decline everything in ' +
      'that bucket at once. Individual accept/decline still works per row.',
  },
};

export function toolbarHint(id) {
  if (!id) return null;
  return TOOLBAR_HINTS[id] || null;
}
